/**
 * The personal capture link — ops.amplispot.com/c/{user}-{token} (ADR-001 §10.2, §11.11; prototype Capture "Open
 * your link on your phone. No app, no login each time."). A phone page outside the session shell: no top bar, no
 * menu, no session cookie needed. It reads the token from the path and calls ONLY the two token routes:
 *
 *   GET  /capture/link?t=         the greeting (first name)
 *   POST /capture/notes?t=        the note, source 'link' — create-only; the 202 is all the page ever reads back
 *
 * The recorder is the in-app Capture screen's (useVoiceRecorder, LiveRecorder): live dictation through the phone
 * browser's speech service (ADR-002 §24.5; iPhones through Apple's, Android Chrome through Google's), with the vendor
 * notice beside Record, at most 2:00, edited before sending. Only the text reaches Ops. Firefox types the note.
 *
 * States: loading; a link that is unknown, rotated or switched off → one plain sentence, with no detail about why
 * (the API answers 404 for all three); rate limited → plain wording; sent → "Got it — it will settle in two minutes".
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ApiFailure } from '../../api';
import { captureLinkInfo, createLinkNote } from '../../lib/capture';
import { errorText, useToast } from '../../ui/kit';
import { Hearing, LiveRecorder } from './LiveRecorder';
import { TOO_SHORT, appendWords, useVoiceRecorder } from './useVoiceRecorder';

/**
 * `/c/{user}-{token}`: the user part is a cosmetic first-name slug (letters and digits, no hyphen) and is never
 * checked; the token is everything after the first hyphen. A bare token (`/c/{token}`) and the older
 * `/capture?t={token}` links work too.
 */
export function tokenFromLink(link: string): string {
  const m = /^([a-z0-9]{1,40})-([A-Za-z0-9_-]{20,64})$/.exec(link);
  return m ? m[2]! : link;
}

type Gone = 'gone' | 'busy';
const goneOf = (e: unknown): Gone | null =>
  e instanceof ApiFailure ? (e.status === 429 ? 'busy' : e.status === 404 || e.status === 403 || e.status === 400 || e.status === 422 ? 'gone' : null) : null;

/** Route element for `/c/:link`. */
export function CaptureLinkRoute() {
  const { link = '' } = useParams();
  return <CaptureLink token={tokenFromLink(link)} />;
}

export function CaptureLink({ token }: { token: string }) {
  useEffect(() => {
    // The token is in this page's address: never send it on as a referrer, and keep the page out of the tab title.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    const title = document.title;
    document.title = 'Capture a note · Amplispot Ops';
    return () => {
      meta.remove();
      document.title = title;
    };
  }, []);

  const info = useQuery({
    queryKey: ['capture-link', token],
    queryFn: () => captureLinkInfo(token),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const [gone, setGone] = useState<Gone | null>(null);
  const [sent, setSent] = useState(0);
  const failed = gone ?? (info.isError ? goneOf(info.error) : null);

  return (
    <div className="signin caplink">
      <div className="signin-wrap">
        <div className="signin-brand">
          <div className="glyph" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </div>
          <div>
            <div className="lg1" style={{ fontWeight: 800, fontSize: 20, lineHeight: 1.05 }}>
              Amplispot
            </div>
            <div className="lg2" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.2em', color: '#B9B2F0', textTransform: 'uppercase' }}>
              Ops
            </div>
          </div>
        </div>
        <div className="signin-card caplink-card">
          {failed === 'gone' ? (
            <Plain title="This link no longer works" body="Ask for a new one." />
          ) : failed === 'busy' ? (
            <Plain
              title="Too many notes from this link"
              body="This link has been used a lot in the last hour. Wait a while, then try again."
              action={gone === 'busy' ? { label: 'Try again', run: () => setGone(null) } : { label: 'Try again', run: () => void info.refetch() }}
            />
          ) : info.isError ? (
            <Plain title="The page did not load" body={errorText(info.error)} action={{ label: 'Try again', run: () => void info.refetch() }} />
          ) : info.isPending ? (
            <div className="empty loading" role="status">
              <span className="spin" aria-hidden="true" />
              Opening your capture link…
            </div>
          ) : sent ? (
            <Sent onAgain={() => setSent(0)} />
          ) : (
            <Compose token={token} first={info.data.first_name} onSent={() => setSent((n) => n + 1)} onGone={setGone} />
          )}
        </div>
      </div>
    </div>
  );
}

function Plain({ title, body, action }: { title: string; body: string; action?: { label: string; run: () => void } }) {
  return (
    <div style={{ display: 'grid', gap: 10, textAlign: 'center' }} role="alert">
      <h1>{title}</h1>
      <p className="lead" style={{ margin: 0 }}>
        {body}
      </p>
      {action ? (
        <button className="btn" onClick={action.run} style={{ justifySelf: 'center' }}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

function Sent({ onAgain }: { onAgain: () => void }) {
  return (
    <div style={{ display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }} role="status">
      <span className="caplink-ok" aria-hidden="true">
        ✓
      </span>
      <h1>Got it — it will settle in two minutes</h1>
      <p className="lead" style={{ margin: 0 }}>
        The tasks show up in Ops for you to check. You can close this page.
      </p>
      <button className="btn pri" onClick={onAgain}>
        Capture another note
      </button>
    </div>
  );
}

function Compose({
  token,
  first,
  onSent,
  onGone,
}: {
  token: string;
  first: string;
  onSent: () => void;
  onGone: (g: Gone) => void;
}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const v = useVoiceRecorder({ onFinal: (t) => setText((x) => appendWords(x, t)) });

  const submit = async () => {
    const t = text.trim();
    if (v.recording) return toast('Stop the recording first');
    if (!t) return toast('Say or type something first');
    if (t.length < 3) return setErr(TOO_SHORT);
    setBusy(true);
    setErr(null);
    try {
      // Only the text the member checked goes, never audio.
      await createLinkNote(token, { text: t, ...v.outgoing() });
      onSent();
    } catch (e) {
      const g = goneOf(e);
      if (g && !(e instanceof ApiFailure && e.code === 'validation_failed')) return onGone(g);
      setErr(e instanceof Error && !('status' in e) ? e.message : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ marginBottom: 14 }}>
        <h1>Hi {first}</h1>
        <p className="lead" style={{ margin: 0 }}>
          Say what happened and what needs doing. It comes to Ops, and you get two minutes to check the tasks.
        </p>
      </div>
      <LiveRecorder v={v} disabled={busy} phone />
      <div className="txhead">
        <span className="lbl">{v.recording ? 'Your note · you can edit it while recording' : 'Your note'}</span>
        <div className="grow" />
        <span className={`cnt${err === TOO_SHORT ? ' bad' : ''}`}>{text.length} characters</span>
      </div>
      <textarea
        className="in"
        rows={5}
        aria-label="Your note"
        placeholder={v.voice && v.why !== 'blocked' ? 'Your words appear here as you speak. You can also type.' : 'Type your note.'}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setErr(null);
        }}
        ref={(el) => {
          if (el && v.recording && document.activeElement !== el) el.scrollTop = el.scrollHeight;
        }}
        maxLength={5000}
      />
      <Hearing v={v} />
      {err ? (
        <div className="note bad" style={{ margin: '6px 0' }}>
          {err}
        </div>
      ) : null}
      <button className="btn pri caplink-send" onClick={() => void submit()} disabled={busy || v.recording} style={{ marginTop: 10 }}>
        {busy ? 'Sending…' : v.recording ? 'Stop the recording to send' : 'Send to Ops'}
      </button>
    </div>
  );
}
